/**
 * RezervacePage — the rental's reservations (`/pujcovna/rezervace`, P3-b): the list of the firma's reservations
 * with their car, customer and term; at `pujcovna:write` create, change and cancel. A reservation sits on top of
 * the fleet entry and the firma's own customer — this page only joins them for display. The car × day calendar
 * is the next slice.
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Badge } from '@/components/Badge';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { IconButton } from '@/components/IconButton';
import { LinkButton } from '@/components/LinkButton';
import { Pill } from '@/components/Pill';
import { Link } from '@tanstack/react-router';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { SegmentedControl } from '@/components/SegmentedControl';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { SearchInput } from '@/components/SearchInput';
import { Toolband } from '@/components/Toolband';
import { formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { pragueToday } from '@/lib/prague-date';
import {
  useFleetQuery,
  usePobockyQuery,
  useRezervaceQuery,
  type FleetEntry,
  type Rezervace,
} from './api';
import { PujcovnaSubtabs } from './PujcovnaSubtabs';
import { PlatbyModal } from './PlatbyModal';
import { PredaniModal } from './PredaniModal';
import { RezervaceModal } from './RezervaceModal';
import { VozidloOdkaz } from './VozidloOdkaz';
import { VraceniModal } from './VraceniModal';
import { useRezervaceVUrl } from './rezervace-url';
import { ZruseniModal } from './ZruseniModal';
import { dnyText } from './tisk-helpers';
import { useServisKalendarQuery } from '@/modules/servis/api';
import { RezervaceKalendar } from './RezervaceKalendar';
import { udalostiPodleVozu } from './servis-udalosti-helpers';
import { zaplacenoStav } from './platby-helpers';
import {
  STREDISKO_LABEL,
  TYP_LABEL,
  addMonths,
  daysOfMonth,
  dnuRezervace,
  duvodJenCteni,
  formatCasPraha,
  formatDateTime,
  jeBlokace,
  lzePredatZFormulare,
  lzePrijmoutZFormulare,
  monthLabel,
  monthOf,
} from './rezervace-helpers';

// the servis module's own event dialog, opened read-only from a calendar bar (lazy: most visits never open it)
const ServisEventModal = lazy(() => import('@/modules/servis/ServisEventModal').then((m) => ({ default: m.ServisEventModal })));

type StavFilter = 'aktivni' | 'zrusena' | 'vse';
type ObdobiFilter = 'budouci' | 'vse';
type Pohled = 'seznam' | 'kalendar';

export function RezervacePage() {
  const { can, resolved } = useModuleAccess();
  const canRead = resolved && can('pujcovna', 'read');
  const canWrite = resolved && can('pujcovna', 'write');
  const canAdmin = resolved && can('pujcovna', 'admin');
  const { isPhone } = useBreakpoint();
  const [stav, setStav] = useState<StavFilter>('aktivni');
  const [obdobi, setObdobi] = useState<ObdobiFilter>('budouci');
  const [pohled, setPohled] = useState<Pohled>('kalendar');
  // the list's search box; the server filters (the list is paged), so it asks only once typing pauses
  const [hledat, setHledat] = useState('');
  const hledatQ = useDebouncedValue(hledat.trim(), 300);
  const today = pragueToday();
  const [mesic, setMesic] = useState(() => monthOf(pragueToday()));
  // One form instance per opened reservation / clicked day: `rezervace` null = a new one, optionally seeded with
  // the clicked car and day (the calendar).
  const [editing, setEditing] = useState<{ rezervace: Rezervace | null; vozidlo?: number; od?: string } | null>(null);
  // The handover dialog (P4-d): opened from a list row or from the reservation's own form (the calendar bar opens that).
  const [predavani, setPredavani] = useState<Rezervace | null>(null);
  // The return dialog (P4-e): the same two doors.
  const [prijimani, setPrijimani] = useState<Rezervace | null>(null);
  // The payments dialog (P6-e2): the way to a reservation's money when its form is closed to edits.
  const [platby, setPlatby] = useState<Rezervace | null>(null);
  // the open form is in the address, so a printout opened from it can come back to it
  useRezervaceVUrl('/pujcovna/rezervace', editing?.rezervace?.id ?? null, (r) => setEditing({ rezervace: r }));

  const fleet = useFleetQuery(canRead);
  const pobocky = usePobockyQuery();
  const list = useRezervaceQuery(
    { stav: stav === 'vse' ? undefined : stav, od: obdobi === 'budouci' ? pragueToday() : undefined, q: hledatQ || undefined },
    canRead && pohled === 'seznam',
  );
  // The calendar's month: every active reservation touching it (the query pages by 100, all pages are fetched).
  const monthDays = useMemo(() => daysOfMonth(mesic), [mesic]);
  const kal = useRezervaceQuery(
    { stav: 'aktivni', od: monthDays[0], do: monthDays[monthDays.length - 1] },
    canRead && pohled === 'kalendar',
  );
  // After a failed page the loaded pages are kept and `hasNextPage` stays true: auto-paging stops there (no retry
  // loop) and the calendar offers an explicit retry instead of drawing an incomplete month. Only the visible
  // calendar pages: a disabled observer's query can still be fetched by hand, so leaving the view must stop it.
  useEffect(() => {
    if (pohled === 'kalendar' && kal.hasNextPage && !kal.isFetchingNextPage && !kal.isFetchNextPageError) {
      void kal.fetchNextPage();
    }
  }, [pohled, kal.hasNextPage, kal.isFetchingNextPage, kal.isFetchNextPageError, kal]);
  // The servis events on the same days (a member who reads servis): an extra layer, so a failed read only leaves it out
  const canServis = resolved && can('servis', 'read');
  const servisKal = useServisKalendarQuery(monthDays[0]!, monthDays[monthDays.length - 1]!, canServis && pohled === 'kalendar');
  const servisUdalosti = useMemo(() => udalostiPodleVozu(fleet.data ?? [], servisKal.data ?? []), [fleet.data, servisKal.data]);
  // The servis event whose detail is open (read-only: it cannot be changed from the rental)
  const [servisDetail, setServisDetail] = useState<number | null>(null);
  const kalRows = useMemo(() => kal.data?.pages.flatMap((p) => p.rezervace) ?? [], [kal.data]);
  const [rusit, setRusit] = useState<Rezervace | null>(null);

  const carById = useMemo(() => new Map((fleet.data ?? []).map((f) => [f.id, f])), [fleet.data]);
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.rezervace) ?? [], [list.data]);
  const total = list.data?.pages[list.data.pages.length - 1]?.total ?? 0;
  // the crown counts what the shown view shows: the calendar's month, not the hidden list's scope
  const calTotal = kal.data?.pages[kal.data.pages.length - 1]?.total ?? 0;
  const crownCount =
    pohled === 'kalendar' ? (kal.isSuccess && !kal.hasNextPage ? String(calTotal) : '—') : list.isSuccess ? String(total) : '—';

  // cancelling asks for a reason (optional) in its own dialog; it is stored with the cancellation and shown in the list of the cancelled
  const doCancel = (r: Rezervace) => setRusit(r);

  // A change needs the car's ACTIVE fleet entry (the server locks it); an ended reservation of a car that has been
  // taken out since can only be cancelled.
  // Printouts (P4-g): the protocol and the signed contract are built from what the handover froze, so they exist only after it. Before it an
  // ACTIVE customer rental offers the contract as a preview (live data, nothing frozen — the sheet says so). Any reader may print; they open in their own tab.
  const tiskLinks = (r: Rezervace) => {
    if (r.stav !== 'aktivni') return null;
    if (!r.predano_at) {
      return r.typ === 'denni' ? (
        <span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1" data-testid={`tisk-${r.id}`}>
          <LinkButton as="a" href={`/pujcovna/rezervace/${r.id}/smlouva`} target="_blank" rel="noreferrer" title="Náhled před předáním — údaje se zmrazí až s předáním.">
            Tisk smlouvy
          </LinkButton>
        </span>
      ) : null;
    }
    return (
      <span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1" data-testid={`tisk-${r.id}`}>
        {r.typ !== 'technicky' && (
          <LinkButton as="a" href={`/pujcovna/rezervace/${r.id}/smlouva`} target="_blank" rel="noreferrer">
            Tisk smlouvy
          </LinkButton>
        )}
        <LinkButton as="a" href={`/pujcovna/rezervace/${r.id}/protokol`} target="_blank" rel="noreferrer">
          Tisk protokolu
        </LinkButton>
      </span>
    );
  };

  // "Platby" for any rental that has a lease or holds money (also a returned or cancelled one: its form is closed, its refunds are not)
  const platbyButton = (r: Rezervace) =>
    !jeBlokace(r.typ) && (r.k_uhrade != null || r.zaplaceno > 0) ? (
      <Button variant="secondary" size="sm" data-testid={`platby-${r.id}`} onClick={() => setPlatby(r)}>
        Platby
      </Button>
    ) : null;

  const rowActions = (r: Rezervace) => {
    const tisk = tiskLinks(r);
    const platbyBtn = platbyButton(r);
    const zmeny = canWrite && r.stav === 'aktivni';
    if (!tisk && !platbyBtn && !zmeny) return null;
    return (
      <span className="flex flex-col items-end gap-1.5">
        {tisk}
        {(zmeny || platbyBtn) && (
          <span className="flex flex-wrap justify-end gap-2">
            {platbyBtn}
            {zmeny && carById.has(r.id_vozidla) && !r.predano_at && r.typ !== 'servis' && (
              <Button variant="secondary" size="sm" onClick={() => setPredavani(r)}>
                Předat vůz
              </Button>
            )}
            {zmeny && carById.has(r.id_vozidla) && r.predano_at && !r.vraceno_at && (
              <Button variant="secondary" size="sm" onClick={() => setPrijimani(r)}>
                Přijmout vozidlo
              </Button>
            )}
            {zmeny && carById.has(r.id_vozidla) && !r.vraceno_at && (
              <Button variant="secondary" size="sm" onClick={() => setEditing({ rezervace: r })}>
                Upravit
              </Button>
            )}
            {/* a car that is out (or back) is closed by the return, never cancelled */}
            {zmeny && !r.predano_at && (
              <Button variant="secondary" size="sm" disabled={rusit != null} onClick={() => doCancel(r)}>
                Zrušit
              </Button>
            )}
          </span>
        )}
      </span>
    );
  };

  const columns: DataTableColumn<Rezervace>[] = [
    {
      id: 'cislo',
      header: 'ID',
      width: '72px',
      priority: 'meta',
      cell: (r) => (
        <span data-testid={`rezervace-cislo-${r.id}`} className="font-mono text-[12px] text-ink-2">
          {r.id}
        </span>
      ),
    },
    {
      id: 'termin',
      header: 'Termín',
      width: 'minmax(230px, 1.3fr)',
      priority: 'primary',
      cell: (r) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="font-mono text-[12.5px] text-ink">
            {formatDateTime(r.od)} – {formatDateTime(r.do)}
          </span>
          <span className="text-[12px] text-ink-3">{dnyText(dnuRezervace(r.od, r.do))}</span>
        </span>
      ),
    },
    {
      id: 'vozidlo',
      header: 'Vozidlo',
      width: 'minmax(180px, 1.2fr)',
      priority: 'secondary',
      cell: (r) => {
        const car: FleetEntry | undefined = carById.get(r.id_vozidla);
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-[13px] text-ink">
              <VozidloOdkaz entry={car}>
                {car?.vozidlo ? [car.vozidlo.znacka_nazev, car.vozidlo.model_nazev].filter(Boolean).join(' ') || 'Vozidlo' : 'Vozidlo'}
              </VozidloOdkaz>
            </span>
            <span className="flex flex-wrap items-center gap-2">
              {car?.vozidlo?.spz && (
                <VozidloOdkaz entry={car}>
                  <Pill>{car.vozidlo.spz}</Pill>
                </VozidloOdkaz>
              )}
              <span className="font-mono text-[11px] text-ink-3">{r.vin}</span>
            </span>
          </span>
        );
      },
    },
    {
      id: 'zakaznik',
      header: 'Zákazník',
      width: 'minmax(160px, 1fr)',
      priority: 'secondary',
      cell: (r) =>
        r.typ === 'technicky' || r.typ === 'servis' ? (
          <Badge tone={r.typ === 'servis' ? 'danger' : 'neutral'} size="sm">
            {r.typ === 'servis' ? 'Servis' : 'Technická rezervace'}
          </Badge>
        ) : (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[13px] text-ink">
              {r.id_zakaznika != null ? (
                <Link to="/zakaznici/$id" params={{ id: String(r.id_zakaznika) }} className="underline-offset-2 hover:underline">
                  {r.zakaznik_nazev ?? '—'}
                </Link>
              ) : (
                (r.zakaznik_nazev ?? '—')
              )}
            </span>
            {r.zakaznik_telefon && <span className="font-mono text-[11px] text-ink-3">{r.zakaznik_telefon}</span>}
          </span>
        ),
    },
    {
      id: 'typ',
      header: 'Typ · poznámka',
      width: 'minmax(170px, 1fr)',
      priority: 'meta',
      cell: (r) => (
        <span className="flex min-w-0 flex-col gap-0.5 text-[12px] text-ink-2">
          <span>
            {TYP_LABEL[r.typ]} · {STREDISKO_LABEL[r.stredisko]}
          </span>
          {r.pojistovna_nazev && <span data-testid={`rezervace-pojistovna-${r.id}`}>Pojišťovna: {r.pojistovna_nazev}</span>}
          {r.stav === 'zrusena' && (
            <span data-testid={`rezervace-zruseni-${r.id}`} className="text-red-text">
              Zrušeno {formatCasPraha(r.zruseno_at)}
              {r.zruseno_duvod ? ` — ${r.zruseno_duvod}` : ''}
            </span>
          )}
          {r.poznamka && (
            <span data-testid={`rezervace-poznamka-${r.id}`} title={r.poznamka} className="truncate text-ink-3">
              {r.poznamka}
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'cena',
      header: 'Cena',
      width: '110px',
      align: 'right',
      priority: 'meta',
      // the total to pay (rent + deposit) as frozen with the reservation; a technical block or an unpriced car has none
      cell: (r) => (
        <span data-testid="rezervace-cena-sloupec" className="font-mono text-[12px] text-ink-2">
          {r.cena ? `${formatInt(r.cena.celkem)} Kč` : '—'}
          {r.doplatek_km_kc != null && <span className="block text-[11px] text-ink-3">+ doplatek {formatInt(r.doplatek_km_kc)} Kč</span>}
        </span>
      ),
    },
    {
      id: 'zaplaceno',
      header: 'Zaplaceno',
      width: '130px',
      align: 'right',
      priority: 'meta',
      // what the payments hold against the lease (P6-e2); the server's balance, only worded here
      cell: (r) => {
        const z = zaplacenoStav(r);
        return (
          <span data-testid={`rezervace-zaplaceno-${r.id}`} className="flex flex-col items-end gap-0.5 font-mono text-[12px] text-ink-2">
            {z.druh === 'bez' ? (
              '—'
            ) : z.druh === 'zaplaceno' ? (
              <>
                <Badge tone="ok" size="sm">
                  Zaplaceno
                </Badge>
                <span className="text-[11px] text-ink-3">{formatInt(z.zaplaceno)} Kč</span>
              </>
            ) : z.druh === 'castecne' ? (
              <>
                <Badge tone="warn" size="sm">
                  Zčásti
                </Badge>
                <span className="text-[11px] text-ink-3">
                  {formatInt(z.zaplaceno)} z {formatInt(z.k_uhrade)} Kč
                </span>
              </>
            ) : (
              <>
                <Badge tone="neutral" size="sm">
                  Nezaplaceno
                </Badge>
                <span className="text-[11px] text-ink-3">{formatInt(z.k_uhrade)} Kč</span>
              </>
            )}
          </span>
        );
      },
    },
    {
      id: 'pobocka',
      header: 'Pobočka',
      width: '130px',
      priority: 'meta',
      cell: (r) => <span className="text-[12px] text-ink-2">{r.pobocka_nazev}</span>,
    },
    {
      id: 'stav',
      header: 'Stav',
      width: '110px',
      priority: 'meta',
      cell: (r) =>
        r.stav === 'aktivni' ? (
          r.po_terminu ? (
            <Badge tone="danger" size="sm">
              Po termínu
            </Badge>
          ) : r.vraceno_at ? (
            <Badge tone="neutral" size="sm">
              Vráceno
            </Badge>
          ) : r.predano_at ? (
            <Badge tone="accent" size="sm">
              Předáno
            </Badge>
          ) : r.typ === 'servis' ? (
            <Badge tone="danger" size="sm">
              V servisu
            </Badge>
          ) : (
            <Badge tone="ok" size="sm">
              Aktivní
            </Badge>
          )
        ) : (
          <Badge tone="neutral" size="sm">
            Zrušená
          </Badge>
        ),
    },
    ...(isPhone
      ? []
      : [
          {
            id: 'akce',
            header: '',
            width: '230px',
            align: 'right',
            priority: 'meta',
            cell: (r: Rezervace) => rowActions(r),
          } satisfies DataTableColumn<Rezervace>,
        ]),
  ];

  const pending = !resolved || list.isPending || fleet.isPending;

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Půjčovna', href: '/pujcovna' }, { label: 'Rezervace' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>
            Rezervace,
            <br />
            <em>termíny a zákazníci.</em>
          </DisplayHeading>
          <CrownMeta items={[{ label: 'Rezervace', value: crownCount }]} />
        </Crown.Row>
        <PujcovnaSubtabs active="rezervace" />
      </Crown>

      <Toolband>
        <SegmentedControl<Pohled>
          aria-label="Pohled"
          value={pohled}
          onChange={setPohled}
          options={[
            { value: 'kalendar', label: 'Kalendář' },
            { value: 'seznam', label: 'Seznam' },
          ]}
        />
        {pohled === 'seznam' ? (
          <>
            <SearchInput
              placeholder="Hledat — číslo, zákazník, SPZ, VIN, vozidlo…"
              aria-label="Hledat rezervaci"
              value={hledat}
              onChange={(e) => setHledat(e.target.value)}
              wrapperClassName="min-w-[220px] max-w-[380px] flex-1"
              data-testid="rezervace-hledat"
            />
            <SegmentedControl<StavFilter>
              aria-label="Stav rezervací"
              value={stav}
              onChange={setStav}
              options={[
                { value: 'aktivni', label: 'Aktivní' },
                { value: 'zrusena', label: 'Zrušené' },
                { value: 'vse', label: 'Vše' },
              ]}
            />
            <SegmentedControl<ObdobiFilter>
              aria-label="Období"
              value={obdobi}
              onChange={setObdobi}
              options={[
                { value: 'budouci', label: 'Od dneška' },
                { value: 'vse', label: 'Všechny' },
              ]}
            />
          </>
        ) : (
          <span className="flex items-center gap-2" data-testid="kal-navigace">
            <IconButton size="sm" aria-label="Předchozí měsíc" onClick={() => setMesic((m) => addMonths(m, -1))}>
              <Icon name="chevronL" size={12} />
            </IconButton>
            <span data-testid="kal-mesic" className="min-w-[130px] text-center font-mono text-[12px] uppercase tracking-[0.06em] text-ink">
              {monthLabel(mesic)}
            </span>
            <IconButton size="sm" aria-label="Další měsíc" onClick={() => setMesic((m) => addMonths(m, 1))}>
              <Icon name="chevronR" size={12} />
            </IconButton>
            <Button variant="secondary" size="sm" onClick={() => setMesic(monthOf(today))}>
              Dnes
            </Button>
          </span>
        )}
        <Toolband.Spacer />
        {canWrite && (
          <Button variant="primary" size="sm" icon={<Icon name="plus" size={12} />} onClick={() => setEditing({ rezervace: null })}>
            Nová rezervace
          </Button>
        )}
      </Toolband>

      <div data-testid="rezervace-page" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
        {!resolved ? (
          <Skeleton className="h-10 w-full" />
        ) : !canRead ? (
          <EmptyState eyebrow="403" title="Rezervace vyžadují přístup k modulu Půjčovna." description="Pro přístup požádejte správce firmy." />
        ) : (pohled === 'kalendar' ? kal.isError && !kal.isFetchNextPageError : list.isError) || fleet.isError ? (
          <EmptyState eyebrow="Chyba" title="Rezervace se nepodařilo načíst" description="Zkuste to prosím znovu za chvíli." />
        ) : pohled === 'kalendar' && kal.isFetchNextPageError ? (
          <EmptyState
            eyebrow="Chyba"
            title="Část rezervací měsíce se nepodařilo načíst"
            description="Kalendář by ukazoval neúplný měsíc, proto ho nekreslíme."
            action={
              <Button variant="secondary" size="sm" onClick={() => void kal.fetchNextPage()}>
                Zkusit znovu
              </Button>
            }
          />
        ) : pohled === 'kalendar' ? (
          fleet.isPending || kal.isPending || kal.hasNextPage ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : (fleet.data ?? []).length === 0 && kalRows.length === 0 ? (
            <EmptyState eyebrow="Bez vozidel" title="V půjčovně zatím není žádné vozidlo" description="Kalendář ukazuje vozidla vozového parku." />
          ) : (
            <RezervaceKalendar
              fleet={fleet.data ?? []}
              rezervace={kalRows}
              month={mesic}
              today={today}
              servisUdalosti={servisUdalosti}
              onOpenServis={setServisDetail}
              canWrite={canWrite}
              onCreate={(idVozidla, day) => setEditing({ rezervace: null, vozidlo: idVozidla, od: day })}
              onOpen={(r) => setEditing({ rezervace: r })}
            />
          )
        ) : pending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            eyebrow="Bez rezervací"
            title={hledatQ ? 'Hledání nic nenašlo' : 'V tomto výběru nejsou žádné rezervace'}
            description={hledatQ ? 'Zkuste jiné slovo, nebo upravte filtr Aktivní / Zrušené a období.' : canWrite ? 'Novou rezervaci založíte tlačítkem „Nová rezervace“ vpravo nahoře.' : 'Rezervace zakládá člen s právem měnit data půjčovny.'}
          />
        ) : (
          <div data-testid="rezervace-seznam" className="flex flex-col gap-3">
            <DataTable<Rezervace>
              aria-label="Rezervace půjčovny"
              variant="bordered"
              columns={columns}
              data={rows}
              getRowId={(r) => String(r.id)}
              renderRowDetail={isPhone ? (r) => rowActions(r) : undefined}
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span data-testid="rezervace-pocet" className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                {rows.length} z {total}
              </span>
              {list.hasNextPage && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  Načíst další ({total - rows.length})
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {servisDetail != null && (
        <Suspense fallback={null}>
          <ServisEventModal open readOnly editId={servisDetail} onOpenChange={(o) => { if (!o) setServisDetail(null); }} />
        </Suspense>
      )}

      {editing && (
        <RezervaceModal
          // a new instance per reservation / clicked day: the form is seeded once from it
          key={editing.rezervace ? `r${editing.rezervace.id}` : `n${editing.vozidlo ?? ''}-${editing.od ?? ''}`}
          rezervace={editing.rezervace}
          defaultVozidlo={editing.vozidlo ?? null}
          defaultOd={editing.od ?? null}
          fleet={fleet.data ?? []}
          pobocky={pobocky.data ?? []}
          canOverride={canAdmin}
          jenCteni={editing.rezervace == null ? undefined : duvodJenCteni(canWrite, carById.has(editing.rezervace.id_vozidla))}
          onPredat={
            editing.rezervace && lzePredatZFormulare(editing.rezervace, canWrite)
              ? () => {
                  setPredavani(editing.rezervace);
                  setEditing(null);
                }
              : undefined
          }
          onPrijmout={
            editing.rezervace && lzePrijmoutZFormulare(editing.rezervace, canWrite)
              ? () => {
                  setPrijimani(editing.rezervace);
                  setEditing(null);
                }
              : undefined
          }
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
      {rusit && (
        <ZruseniModal
          key={rusit.id}
          rezervace={rusit}
          title={carById.get(rusit.id_vozidla)?.vozidlo?.spz || rusit.vin}
          open
          onOpenChange={(o) => !o && setRusit(null)}
        />
      )}
      {platby && (
        <PlatbyModal
          // one instance per reservation
          key={platby.id}
          rezervace={platby}
          title={carById.get(platby.id_vozidla)?.vozidlo?.spz || platby.vin}
          canWrite={canWrite}
          canAdmin={canAdmin}
          open
          onOpenChange={(open) => !open && setPlatby(null)}
        />
      )}
      {predavani && (
        <PredaniModal
          // one instance per reservation: its form is built once from the reads made after it opened
          key={predavani.id}
          rezervace={predavani}
          title={carById.get(predavani.id_vozidla)?.vozidlo?.spz || predavani.vin}
          canOverride={canAdmin}
          open
          onOpenChange={(open) => !open && setPredavani(null)}
        />
      )}
      {prijimani && (
        <VraceniModal
          // one instance per reservation: its form is built once from the reads made after it opened
          key={prijimani.id}
          rezervace={prijimani}
          title={carById.get(prijimani.id_vozidla)?.vozidlo?.spz || prijimani.vin}
          open
          onOpenChange={(open) => !open && setPrijimani(null)}
        />
      )}
    </Shell>
  );
}

